<template>
    <div class="log-table">
        <design-2021-apprentice-data-table
            class="log-data-table"
            v-if="tableItems.length"
            :sort-by="'unix'"
            :sort-desc="true"
            :fields="columns"
            :search-term="searchIn"
            :items="tableItems">
            <template v-slot:options>
                <div class="actions">
                    <b-button
                        class="btn-download btn-download-logbook"
                        variant="primary"
                        @click="downloadList()">
                        {{ $t('log.download_list') }}
                    </b-button>

                    <b-button
                        class="btn-add btn-add-logbook"
                        variant="primary"
                        @click="showLogMutationModal">
                        {{ $t('log.new_log_book_item') }}
                    </b-button>
                </div>
            </template>

            <template v-slot:cell(unix)="row">
                <div class="date-time">
                    {{ row.item.date_time | date_time_om }}
                </div>
            </template>

            <template v-slot:cell(difference)="row">
                <div class="time">
                    {{ row.item.difference }}
                </div>
            </template>

            <template v-slot:cell(updated_at)="row">
                <div class="server-time">
                    {{ row.item.updated_at | date_time_om }}
                </div>
            </template>

            <template v-slot:cell(description)="row">
                <div class="description">
                    {{ row.item.description }}
                </div>
            </template>

            <template v-slot:cell(reflection)="row">
                <div class="reflection">
                    {{ row.item.reflection }}
                </div>
            </template>

            <template v-slot:cell(actions)="row">
                <div class="row-actions">
                    <b-button
                        class="btn-edit btn-edit-logbook remove-icon-margin"
                        v-if="$authUser.isAdmin() || $authUser.isTeacher() || row.item.created_by === $authUser.id"
                        variant="primary-icon-only"
                        @click="fetchRow(row.item.id)">
                    </b-button>
                    <b-button
                        class="btn-remove btn-delete-logbook remove-icon-margin"
                        v-if="canDelete(row.item.created_by)"
                        variant="danger-icon-only"
                        @click="deleteRow(row.item.id)">
                    </b-button>
                </div>
            </template>
        </design-2021-apprentice-data-table>

        <div
            class="not-found with-header no-logbooks"
            v-else>
            <div
                class="btn-add-circle icon"
                v-b-tooltip.hover
                v-if="!$authUser.isTeacher()"
                @click="showLogMutationModal"
                :title="$t('log.add_log_book_item')"></div>
            <div class="alert-notice">{{ $t('log.no_log_book_found') }}</div>
        </div>

        <design-2021-log-data-mutation-modal-component
            :students-in="students"
            model-data="model"
            ref="logdata-modal"
            @mutation-update="mutateLogbook">
        </design-2021-log-data-mutation-modal-component>
    </div>
</template>

<script>
import { LogBus } from '../../app';
import moment from 'moment';

export default {
    props: ['logData', 'projectIn', 'userId', 'searchIn'],
    name: 'design-2021-log-data-table-component',
    data() {
        return {
            modal: {
                modalTitle: 'Nieuw logboek item',
                id: '',
                date: '',
                location: '',
                start_time: '',
                end_time: '',
                description: '',
                reflection: '',
                edit: false,
                errors: [],
                user: this.$authUser.isStudent() ? this.$authUser : null,
            },

            columns: [
                {
                    key: 'name',
                    label: this.$t('common.name'),
                    sortable: true,
                },
                {
                    key: 'unix',
                    label: this.$t('log.date'),
                    sortable: true,
                },
                {
                    key: 'difference',
                    label: this.$t('log.time'),
                },
                {
                    key: 'location',
                    label: this.$t('common.location'),
                },
                {
                    key: 'description',
                    label: this.$t('common.description'),
                },
                {
                    key: 'reflection',
                    label: this.$t('log.reflection'),
                },
                {
                    key: 'updated_at',
                    label: this.$t('log.edited'),
                },
                {
                    key: 'actions',
                    label: this.$t('common.actions'),
                },
            ],
            tableData: this.logData,
            options: {
                headings: {},
                sortable: ['name', this.$t('common.actions')],
                filterable: false,
            },
            errorMsg: null,
            hasErrors: false,
        };
    },
    mounted() {
        this.$root.$on('bv::modal::show', () => {
            this.errorMsg = null;
            this.hasErrors = false;
        });
    },
    computed: {
        tableItems() {
            if (!this.tableData) {
                return [];
            }

            return this.tableData.map((item) => {
                const obj = {
                    time_difference: this.getTimeDifference(item.time_timestamp, item.time_end_timestamp),
                    unix: Date.parse(item.date_time),
                };
                return { ...obj, ...item };
            });
        },
        students() {
            let students = this.projectIn.users.filter((user) => user.type === 'student');
            if (!this.$authUser.isStudent()) {
                this.modal.user = students[0];
            }
            return students;
        },
    },
    methods: {
        canDelete(userId) {
            if (!this.$authUser.isStudent()) {
                return true;
            }
            return this.$authUser.id === userId;
        },
        getTimeDifference(time_timestamp, time_end_timestamp) {
            let total_amount_in_minutes =
                time_timestamp > time_end_timestamp
                    ? moment.unix(time_end_timestamp).add(1, 'day').diff(moment.unix(time_timestamp), 'minutes')
                    : moment.unix(time_end_timestamp).diff(moment.unix(time_timestamp), 'minutes');

            let hours = Math.round(total_amount_in_minutes / 60);
            const minutes_in_hours = hours * 60;

            if (minutes_in_hours > total_amount_in_minutes) {
                hours = hours - 1;
                total_amount_in_minutes = total_amount_in_minutes + 60;
            }

            const minutes = total_amount_in_minutes - minutes_in_hours;

            if (hours > 0 && minutes > 0) {
                return hours + ' uur, ' + minutes + ' min';
            } else if (hours > 1) {
                return hours + ' uur';
            } else if (minutes > 0) {
                return minutes + ' min';
            }
        },
        mutateLogbook() {
            let row = {
                description: this.modal.description,
                reflection: this.modal.reflection,
                start_time: this.modal.start_time,
                end_time: this.modal.end_time,
                location: this.modal.location,
                date: this.modal.date,
                modalTitle: this.modal.modalTitle,
                user_id: this.modal.user.id,
            };

            if (this.modal.end_time < this.modal.start_time) {
                let date = new Date(row.date);
                let day = date.getDate();
                date.setDate(day + 1);
                row.end_date =
                    date.toLocaleString('default', { year: 'numeric' }) +
                    '-' +
                    date.toLocaleString('default', { month: '2-digit' }) +
                    '-' +
                    date.toLocaleString('default', { day: '2-digit' });
            }

            if (this.modal.edit) {
                row.id = this.modal.id;
                row.modalTitle = this.$t('log.edit_log_book_item');

                this.alert.saving();

                this.axios.post(this.routes.logEdit(this.projectIn.id), row).then(
                    (response) => {
                        if (response.data) {
                            this.alert.success();

                            this.tableData = this.tableData.map((item) => {
                                if (item.id === response.data.log.id) {
                                    return response.data.log;
                                }

                                return item;
                            });

                            this.tableData.sort((a, b) => {
                                return new Date(b.date_time) - new Date(a.date_time);
                            });

                            LogBus.$emit('pushData', '');

                            this.onReset();
                        }
                    },
                    (error) => {
                        this.alert.ajaxError(error);
                    },
                );
            } else {
                row.modalTitle = this.$t('log.new_log_book_item');
                this.alert.saving();
                let uri = '/log/store/' + this.projectIn.id;

                this.axios.post(uri, row).then(
                    (response) => {
                        if (response.data) {
                            this.tableData.push(response.data.log);

                            this.tableData.sort((a, b) => {
                                return new Date(b.date_time) - new Date(a.date_time);
                            });

                            this.alert.success();

                            LogBus.$emit('pushData', '');

                            this.onReset();
                        }
                    },
                    (error) => {
                        this.alert.ajaxError(error);
                    },
                );
            }
        },
        fetchRow(id) {
            let uri = '/log/fetch/' + id;

            this.axios.get(uri).then((response) => {
                let data = response.data;

                if (data) {
                    this.modal.id = id;
                    this.modal.modalTitle = this.$t('log.edit_log_book_item');
                    this.modal.date = data.date;
                    this.modal.location = data.location;
                    this.modal.start_time = data.time;
                    this.modal.end_time = data.time_end;
                    this.modal.description = data.description;
                    this.modal.reflection = data.reflection;
                }
            });

            this.modal.edit = true;
            // Show the modal
            this.$refs['logdata-modal'].onShow(this.modal);
        },
        onReset() {
            this.modal.modalTitle = this.$t('log.new_log_book_item');
            this.modal.id = '';
            this.modal.date = '';
            this.modal.location = '';
            this.modal.start_time = '';
            this.modal.end_time = '';
            this.modal.description = '';
            this.modal.reflection = '';
            this.modal.edit = false;
        },
        deleteRow(id) {
            this.alert
                .confirm(this.$t('log.delete_item'), this.$t('common.are_you_sure_you_want_to_delete_this_item'))
                .then((confirm) => {
                    if (!confirm) {
                        return;
                    }
                    this.deleteSource(id);
                });
        },

        deleteSource(id) {
            let uri = '/log/delete/' + id;

            this.axios.delete(uri).then(
                (response) => {
                    this.tableData = this.tableData.filter((item) => {
                        return item.id !== id;
                    });
                    LogBus.$emit('pushData', id);
                    this.alert.success();
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },
        showLogMutationModal() {
            this.$refs['logdata-modal'].onShow(this.modal);
        },
        downloadList() {
            window.location.href = this.routes.baseUrl() + '/log/download/' + this.projectIn.id;
        },
    },
};
</script>

<style scoped lang="scss">
.date-time,
.time,
.server-time {
    white-space: nowrap;
}

.log-table {
    height: auto;
}

.actions {
    display: flex;
    flex-direction: column;
    flex: 1;

    .btn:not(:last-child) {
        margin-right: 0;
        margin-bottom: map-get($grid-gutter-spacing, 'eight');
    }
}

.row-actions {
    display: flex;
    align-items: center;
    align-content: center;

    .btn {
        &:not(:last-child) {
            margin-right: map-get($grid-gutter-spacing, 'fifteen');
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'md')) {
    .actions {
        flex-direction: row;

        .btn:not(:last-child) {
            margin-right: map-get($grid-gutter-spacing, 'fifteen');
            margin-bottom: 0;
        }
    }

    .log-data-table ::v-deep {
        table {
            white-space: nowrap;
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'md')) {
    .log-data-table ::v-deep {
        table {
            white-space: normal;
        }
    }
}
</style>
